'use client';
import { useState, type ReactElement } from 'react';
import { useTranslation } from '../../lib/i18n';
import { useBrainExecutionTargets } from '../../lib/queries';
import { apiErrorMessage } from '../../lib/elowenClient';
import { openBrainComposer } from '../../lib/brainDock';
import { useToast } from '../../components/ui/Toast';
import { ProjectMenuContent } from '../../components/ui/ProjectMenu';
import { DropdownMenu, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../../components/ui/shadcn/dropdown-menu';
import type { SwitchableProject } from '../../../src/shared/wireContract';
import { useBrainChat } from './BrainChatProvider';

/** Every "new conversation" control in the app: the control is the trigger of an anchored menu listing the
 *  account's projects, and choosing one creates the conversation in that project through the controller's
 *  `startNewConversation({ target })` (the same path the dashboard composer and voice calls take), then
 *  reveals the chat with its composer focused: on the /chat page, or in the dock for the dock's own control
 *  (`dock`). `onChosen` lets a host get out of the way first, such as the conversation switcher closing
 *  itself or the phone overflow menu folding away. */
export function NewConversationMenu({ children, align = 'end', onChosen, dock = false }: {
  /** The new-conversation control; it becomes the menu trigger. */
  children: ReactElement;
  align?: 'start' | 'end';
  onChosen?: () => void;
  dock?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { startNewConversation } = useBrainChat();
  const projects = useBrainExecutionTargets();
  const [pending, setPending] = useState(false);
  const label = t.brainChat.newConversationProject.title;

  const choose = async (project: SwitchableProject) => {
    onChosen?.();
    setPending(true);
    try {
      await startNewConversation({ target: project.executionRef });
      openBrainComposer('', { dock });
    } catch (error) {
      toast(apiErrorMessage(error) || t.brainChat.newChatError, 'error');
    } finally {
      setPending(false);
    }
  };

  // The list is GET /brain/execution's, in the account's order, so the first project is the default the
  // daemon picks when nobody chooses. Explicit local choices retain their opaque target identity.
  const status = projects.isError ? (
    <>
      <DropdownMenuLabel className="text-destructive">{t.projects.loadError}</DropdownMenuLabel>
      <DropdownMenuItem onSelect={(event) => { event.preventDefault(); void projects.refetch(); }}>{t.common.retry}</DropdownMenuItem>
    </>
  ) : projects.isLoading ? (
    <DropdownMenuLabel>{t.common.loading}</DropdownMenuLabel>
  ) : !projects.data?.length ? (
    <DropdownMenuLabel className="italic">{t.brainChat.newConversationProject.empty}</DropdownMenuLabel>
  ) : undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <ProjectMenuContent label={label} align={align} data-testid="new-conversation-menu"
        projects={projects.data ?? []} showKind disabled={pending} status={status}
        onChoose={(project) => { void choose(project); }} />
    </DropdownMenu>
  );
}
